import config from '@plone/volto/registry';
import { Node } from 'slate';
import { getAllBlocks } from '@plone/volto-slate/utils';
import { escapeRegExp } from 'lodash';
import { UniversalLink } from '@plone/volto/components';

const protocol = '((http|https|ftp):\\/\\/)?';
const domain = '([a-zA-Z0-9-]+\\.)+[a-zA-Z]{2,}';
const port = '(:\\d+)?';
const fileExtensions =
  'pdf|doc|docx|xls|xlsx|png|jpg|jpeg|gif|htm|html|xml|txt|csv|zip|ppt|pptx';
const pathWithFile = `(\\/[^<>]*\\.(${fileExtensions})(?=[,;.!?\\s)]|$)|\\/[^\\s<>]*)?`;
const queryString = '(\\?[^\\s<>]*)?';
const trailingPunctuation = '(?=[\\s,;.!?]|$|\\)[\\s,;.!?]|\\)$)';

const urlRegex = new RegExp(
  `\\b${protocol}${domain}${port}${pathWithFile}${queryString}${trailingPunctuation}`,
  'gi',
);

/**
 * retrive all slate children of nested objects
 * @param {object} path - the keys that we want to extract the slate children from
 * @param {*} value - the source that we want to extract the slate children from
 * Exemple of parameters
 * path:{items:'value'}
 * @returns string
 */
export const retriveValuesOfSlateFromNestedPath = (path, value) => {
  if (Array.isArray(value)) {
    let allSlateValue = [];
    value.forEach((element) => {
      allSlateValue = [
        ...allSlateValue,
        ...retriveValuesOfSlateFromNestedPath(path, element),
      ];
    });
    return allSlateValue;
  }
  if (typeof path === 'string' && value) {
    if (value[path]?.length > 0) return [...value[path]];
    return [];
  }
  if (typeof path === 'object' && Object.keys(path).length > 0) {
    return retriveValuesOfSlateFromNestedPath(
      path[Object.keys(path)[0]],
      value[Object.keys(path)[0]],
    );
  }
  return [];
};

/**
 * Will open accordion if contains footnote reference
 * @param {string} footnoteId
 */
export const openAccordionOrTabIfContainsFootnoteReference = (footnoteId) => {
  if (typeof window !== 'undefined') {
    const footnote = document.querySelector(footnoteId);

    if (footnote !== null && footnote.closest('.accordion') !== null) {
      const comp = footnote.closest('.accordion').querySelector('.title');
      if (!comp.className.includes('active')) {
        comp.click();
      }
    }

    if (footnote !== null && footnote.closest('.tab-name') !== null) {
      const idOfTabBlock = footnote.closest('.tabs-block').id;

      const idOfTabPanel = footnote.closest('.tab-name').id;
      const allTabs = document
        .getElementById(idOfTabBlock)
        .getElementsByClassName('menu-item-text');

      (Array.from(allTabs) || []).forEach((tab) => {
        if (
          tab.textContent === idOfTabPanel &&
          !tab.parentElement.classList.contains('active')
        ) {
          tab.parentElement.click();
        }
      });
    }
  }

  return true;
};

/**
 * Will open accordion if contains footnote reference
 * @param {string} footnoteId
 * @deprecated This function got renamed to {@link openAccordionOrTabIfContainsFootnoteReference}
 */
export const openAccordionIfContainsFootnoteReference =
  openAccordionOrTabIfContainsFootnoteReference;

const blockTypesOperations = {
  metadataSection: (block, properties) => {
    const fields = block?.fields || [];
    return fields
      .filter((field) => field?.field?.widget === 'slate')
      .reduce((accumulator, currentField) => {
        const fieldId = currentField.field.id;
        const propertiesBlocks = (properties[fieldId] || []).map(
          (propertyBlockValue) => ({
            '@type': 'slate',
            id: fieldId,
            value: [propertyBlockValue],
          }),
        );
        return [...accumulator, ...propertiesBlocks];
      }, []);
  },

  metadata: (block, properties) => {
    const fId = block?.data?.id;
    return block?.data?.widget === 'slate'
      ? [
          {
            '@type': 'slate',
            id: fId,
            value: properties[fId]?.length ? properties[fId] : null,
          },
        ]
      : [];
  },
  slateTable: (block) => {
    return (block?.table?.rows || []).reduce((accumulator, currentRow) => {
      const cellsBlocks = (currentRow.cells || []).map((cell) => ({
        '@type': 'slate',
        ...cell,
      }));
      return [...accumulator, ...cellsBlocks];
    }, []);
  },
  defaultOperation: (block) => {
    return [block];
  },
};

/**
 * Extends volto-slate getAllBlocks functionality also to SlateJSONFields
 * inserted within blocks via Metadata / Metadata section block
 * @param {Object} properties metadata properties received by the View component
 * @returns {Array} Returns a flat array of blocks and slate fields
 */
export const getAllBlocksAndSlateFields = (properties) => {
  const blocks = getAllBlocks(properties, []);

  return blocks.reduce((accumulator, currentblock) => {
    return [
      ...accumulator,
      ...(blockTypesOperations[currentblock['@type']]
        ? blockTypesOperations[currentblock['@type']](currentblock, properties)
        : blockTypesOperations.defaultOperation(currentblock)),
    ];
  }, []);
};

/**
 * Will make an object with keys for every zoteroId and some uid that are unique
 * or referenced multiple times
 * - objects will have a refs object if more footnotes reference the same one
 * - for citations, same zoteroId
 * - for footnotes, identical text
 * @param {Object} blocks
 * @returns {Object} notesObjResult
 */
export const makeFootnoteListOfUniqueItems = (blocks) => {
  const { footnotes } = config.settings;
  let notesObjResult = {};

  blocks
    .filter((b) => b['@type'] in config.settings.blocksWithFootnotesSupport)
    .forEach((element) => {
      const mapping = config.settings.blocksWithFootnotesSupport[
        element['@type']
      ] || ['value'];

      mapping.forEach((key) => {
        const value = retriveValuesOfSlateFromNestedPath(key, element);

        if (!value) return;

        value
          .filter((val) => val.children)
          .forEach((item) => {
            // Node.elements(item) returns an iterable generator of nodes
            Array.from(Node.elements(item)).forEach(([node]) => {
              if (footnotes.includes(node.type) && node.data) {
                // for citations (Zotero items) create refs for same zoteroId
                if (node.data.zoteroId) {
                  iterateZoteroObj(notesObjResult, node.data);
                  // itereate the extra obj for multiple citations
                  if (node.data.extra) {
                    node.data.extra.forEach((zoteroObjItem) =>
                      // send the uid of the parent
                      // of the word the will have the reference indice
                      iterateZoteroObj(
                        notesObjResult,
                        zoteroObjItem,
                        node.data.uid,
                      ),
                    );
                  }
                  // for footnotes - create refs, on identical text
                } else {
                  iterateFootnoteObj(notesObjResult, node.data);
                  if (node.data.extra) {
                    node.data.extra.forEach((footnoteObjItem) =>
                      // since is called in case of extra, the parent is needed
                      iterateFootnoteObj(
                        notesObjResult,
                        footnoteObjItem,
                        node.data.uid,
                      ),
                    );
                  }
                }
              }
            });
          });
      });
    });

  return notesObjResult;
};

/**
 * Will change the notesObjResultTemp to add new property if the zoteroId is new or add to the existing ones refs
 * @param {Object} notesObjResultTemp - the object that will configure the zotero items
 * @param {Object} zoteroObj - the footnote object
 * @param {string} zoteroObj.zoteroId - id of the zotero citation
 * @param {string} zoteroObj.uid - id of the slate item
 * @param {string} zoteroObj.footnote - xml citation from zotero
 * @param {string} parentUid - will be needed because html element (the word) that references multiple citations
 * will have the id as the main uid, the ids from the extra will not matter in this case
 */
const iterateZoteroObj = (notesObjResultTemp, zoteroObj, parentUid) => {
  const uid = parentUid || zoteroObj.uid;
  // add new zoteroId
  if (!notesObjResultTemp[zoteroObj.zoteroId]) {
    notesObjResultTemp[zoteroObj.zoteroId] = {
      ...zoteroObj,
      uid,
    };
    // if zoteroId and refs exist then add the uid to the refs
  } else if (notesObjResultTemp[zoteroObj.zoteroId].refs) {
    notesObjResultTemp[zoteroObj.zoteroId].refs[uid] = true;
  } else {
    // if zoteroId exists but not refs, add its own uid also in refs for easier parsing in html
    notesObjResultTemp[zoteroObj.zoteroId].refs = {
      [notesObjResultTemp[zoteroObj.zoteroId].uid]: true,
      [uid]: true,
    };
  }
};

/**
 * Will change the notesObjResultTemp to add new property if the footnote uid is new or add to the refs of the existing ones
 * Some footnotes will always be in extra, so we need parentId to know where to find it in render
 * @param {Object} notesObjResultTemp - the object that will configure the zotero items
 * @param {Object} node - the footnote object
 * @param {string} node.zoteroId - id of the zotero citation
 * @param {string} node.parentUid - id of the parent footnote
 * @param {string} node.uid - id of the slate item
 * @param {string} node.footnote - xml citation from zotero
 * @param {string} parentUid - will be needed because html element (the word) that references multiple citations
 * will have the id as the main uid, the ids from the extra will not matter in this case
 */
const iterateFootnoteObj = (notesObjResultTemp, node, parentUid) => {
  const uid = parentUid || node.uid;
  const found = Object.keys(notesObjResultTemp).find((noteId) => {
    return notesObjResultTemp[noteId].footnote === node.footnote;
  });
  // has not yet been added

  if (!found) {
    // will use the parentUid instead of own uid for render to be able to reference to the correct element
    //(word containing the footnotes)
    notesObjResultTemp[node.uid] = parentUid
      ? { ...node, parentUid }
      : { ...node };
    // the element is found, just add it's own uid to the list of refs, the parent is already known
  } else if (notesObjResultTemp[found].refs) {
    notesObjResultTemp[found].refs[uid] = true;
  } else {
    // element found but doesn't have refs yet, this means that it is a parent, so add it's existing uid and the current one
    notesObjResultTemp[found].refs = {
      [found]: true,
      [uid]: true,
    };
  }
};

export function isValidHTML(htmlString) {
  if (
    __CLIENT__ &&
    typeof window !== 'undefined' &&
    typeof DOMParser !== 'undefined'
  ) {
    // The environment is client-side, and DOMParser is available
    const parser = new DOMParser();
    const parsedDocument = parser.parseFromString(htmlString, 'text/html');
    const errors = parsedDocument.querySelectorAll('parsererror');
    return errors.length === 0;
  }
  return false;
}

const cleanUrls = (urls, text) => {
  if (!urls) return urls;

  return urls.map((url) => {
    // Handle URLs ending with punctuation that should not be part of the URL
    // Remove trailing punctuation if it's followed by whitespace or end of string
    const trailingPunctuationMatch = url.match(/^(.+?)([.!?;,]+)$/);
    if (trailingPunctuationMatch) {
      const [, urlPart] = trailingPunctuationMatch;
      const urlIndex = text.indexOf(url);
      const afterUrl = text.substring(urlIndex + url.length);

      // If punctuation is followed by whitespace or end of string, remove it
      if (afterUrl.match(/^\s/) || afterUrl === '') {
        return urlPart;
      }
    }

    // Handle URLs that end with unmatched closing parenthesis
    // This happens when a URL is wrapped in parentheses like "(https://example.com)"
    if (url.endsWith(')')) {
      const urlIndex = text.indexOf(url);
      const beforeUrl = text.substring(0, urlIndex);

      // Check if this closing parenthesis is unmatched (URL is wrapped in parentheses)
      const openParensInUrl = (url.match(/\(/g) || []).length;
      const closeParensInUrl = (url.match(/\)/g) || []).length;

      // If there's an extra closing parenthesis and the URL is preceded by an opening parenthesis
      if (closeParensInUrl > openParensInUrl && beforeUrl.endsWith('(')) {
        return url.slice(0, -1); // Remove the trailing closing parenthesis
      }
    }

    return url;
  });
};

export const renderTextWithLinks = (text, zoteroId) => {
  if (!text) return null;

  const rawLinks = text.match(urlRegex);
  const links = cleanUrls(rawLinks, text);

  let isValid = false;
  if (zoteroId && isValidHTML(text)) isValid = true;

  if (!links) {
    if (isValid)
      return (
        <span
          dangerouslySetInnerHTML={{
            __html: text,
          }}
        />
      );
    else return text;
  }
  let result = [];
  const parts = text.split(
    new RegExp(`(${links.map((link) => escapeRegExp(link)).join('|')})`),
  );
  parts.forEach((part, index) => {
    if (links.includes(part) && zoteroId) {
      result.push(`
          <a key=link-${index} href=${part} rel="noopener">
            ${part}
          </a>`);
      return;
    } else if (links.includes(part)) {
      result.push(
        <UniversalLink
          key={`link-${index}`}
          href={part}
          openLinkInNewTab={false}
        >
          {part}
        </UniversalLink>,
      );
      return;
    } else result.push(part);
  });

  if (isValid)
    return (
      <span
        dangerouslySetInnerHTML={{
          __html: result.reduce((acc, c) => acc + c, ''),
        }}
      />
    );
  else return <div className="description-content">{result}</div>;
};
